/**
 * Curated scene presets — the *primary* visual entry point for users.
 *
 * Each scene is a complete, opinionated configuration. It explicitly sets:
 *   - Which visual LAYERS are on (particles / strings / lattice / aurora / warp / cathedral)
 *   - Which beat overlays (shockwave bubble, starbursts, bloom)
 *   - Per-particle palette (colorMode)
 *   - Camera mode + framing
 *   - Atmosphere, audio reactivity, intensity
 *
 * The contract: tapping a scene results in a coherent, named "look" that's
 * been hand-tuned. Users can flip individual Layer toggles afterward to
 * add/remove elements without losing the scene's other tuning.
 */

import { BioclastParams } from "./bioclast";
import type { AltSceneType } from "../altScenes";

export type SceneControlProfile =
  | "bioclast"
  | "energyOrb"
  | "texturedSphere"
  | "mathParticles"
  | "mesh";

/**
 * Mood buckets — high-level emotional grouping used by the UI to render
 * scenes in clusters instead of one giant flat grid. Optional on each
 * scene so legacy callers don't break.
 */
export type SceneMood =
  | "Math Based"
  | "Chill"
  | "Hype"
  | "Dreamy"
  | "Trippy"
  | "Cinematic"
  | "Cosmic"
  | "Tech";

/**
 * A *delta* applied on top of the parent scene's `values`. Variants let
 * a single scene express 2–5 closely related "looks" (e.g. *Liquid Mass*
 * has Cool/Warm/Hot variants) without duplicating 30 lines of params.
 *
 * Keep variants small — only override the fields that should change.
 * The runtime always re-applies the parent first, then the variant,
 * so removing a variant cleanly reverts to the base scene.
 */
export interface SceneVariant {
  /** Short user-facing label, e.g. "Warm", "Cinema", "Pulse". */
  name: string;
  /** Optional hue override for the chip. Falls back to the parent accent. */
  accent?: number;
  /** Param overrides merged on top of the parent scene's `values`. */
  values: Partial<BioclastParams>;
}

export interface ScenePreset {
  name: string;
  subtitle: string;
  accent: number; // hue 0..360
  /** Renderer engine used by this Scene-tab recipe. Defaults to Bioclast. */
  sceneType?: "bioclast" | AltSceneType;
  /** Controls that should be offered when this scene is selected. */
  controlProfile?: SceneControlProfile;
  /** Optional mood bucket for grouping. */
  mood?: SceneMood;
  values: Partial<BioclastParams>;
  /**
   * Optional named variants. The first ("Original") is implicit and is
   * synthesized at the call site — variants here are *deltas* only.
   */
  variants?: SceneVariant[];
}

/**
 * Hand-curated scenes. Each one explicitly sets every layer toggle so two
 * taps in a row never produce a chimera state.
 */
export const SCENES: ScenePreset[] = [
  // ----------------------------------------------------------------
  // ROW 1 — Universal-appeal defaults
  // ----------------------------------------------------------------
  {
    name: "Liquid Mass",
    subtitle: "Cohesive cloud, gentle swirl",
    accent: 195,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 1.0,
      cameraSpin: 0.4,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.92,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.6,
      trebleShimmer: 1.0,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.95,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
      showShockwave: false,
      showStarbursts: true,
    },
  },
  {
    name: "Aurora",
    subtitle: "Ribbon drifting overhead",
    accent: 165,
    values: {
      // Particles + aurora only — the previous scene had strings + lattice
      // on top, which made the cloud read as visual chaos.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: true,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 1,
      conceptMode: "aurora",
      cameraMode: "orbit",
      cameraTilt: 0.35,
      cameraZoom: 0.9,
      cameraSpin: 0.3,
      starSize: 0.16,
      intensity: 1.2,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 0.9,
      midCurl: 1.4,
      trebleShimmer: 1.6,
      cymaticStrength: 1.0,
      flowSpeed: 0.7,
      fieldTightness: 1.1,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Warp Drive",
    subtitle: "Pure forward velocity, just the tunnel",
    accent: 215,
    values: {
      // Tunnel ONLY. Adding the particle field on top duplicates the "stars
      // rushing past" idea and reads as visual noise instead of speed.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: true,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "warp",
      cameraMode: "flythrough",
      flythroughSpeed: 0,  // static — user positions it and it stays put
      beatDolly: 0,
      cameraZoom: 1.0,
      cameraTilt: 0.5,
      cameraSpin: 0,
      starSize: 0.16,
      intensity: 1.5,
      opacity: 0.95,
      saturation: 0.8,
      bassGravity: 0.8,
      midCurl: 0.6,
      trebleShimmer: 1.4,
      cymaticStrength: 0.9,
      flowSpeed: 2.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Cathedral",
    subtitle: "Architectural pillars, orbit-able",
    accent: 175,
    values: {
      // Pillars only. Particle cloud OFF — the cathedral lines have no
      // substance, so layering them over a swirling cloud reads as visual
      // chaos.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: true,
      colorMode: 3,
      conceptMode: "cathedral",
      // Orbit camera framed slightly above and tilted down, looking at
      // the nave from the side. Slow auto-spin lets the user watch the
      // architecture rotate without doing anything; pinch / drag to
      // explore from any angle.
      cameraMode: "orbit",
      cameraTilt: 0.4,
      cameraZoom: 0.6,    // pulled back so the whole nave fits comfortably
      cameraSpin: 0,      // static — user positions it and it stays put
      starSize: 0.13,
      intensity: 1.25,
      opacity: 0.95,
      saturation: 0.7,
      bassGravity: 1.0,
      midCurl: 0.5,
      trebleShimmer: 0.6,
      cymaticStrength: 1.0,
      flowSpeed: 0.4,
      fieldTightness: 0.9,
      atmosphere: 0.45,
      backdropBlur: 30,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 2 — Mood / quiet looks
  // ----------------------------------------------------------------
  {
    name: "Ghost Cloud",
    subtitle: "Calm, ethereal, low motion",
    accent: 274,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 0,
      conceptMode: "nebula",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.18,
      starSize: 0.18,
      intensity: 1.05,
      opacity: 0.78,
      saturation: 0.7,
      bassGravity: 0.6,
      midCurl: 0.5,
      trebleShimmer: 1.1,
      cymaticStrength: 0.6,
      flowSpeed: 0.22,
      fieldTightness: 1.0,
      atmosphere: 0.4,
      backdropBlur: 70,
      bloomEnabled: true,
      bloomStrength: 0.7,
      bloomRadius: 0.65,
      bloomThreshold: 0.22,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Nebula",
    subtitle: "Wide, slow, deep-space drift",
    accent: 245,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 3,
      conceptMode: "nebula",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.7,
      cameraSpin: 0.02,
      starSize: 0.2,
      intensity: 1.1,
      opacity: 0.8,
      saturation: 0.75,
      bassGravity: 0.7,
      midCurl: 0.4,
      trebleShimmer: 0.9,
      cymaticStrength: 0.7,
      flowSpeed: 0.3,
      fieldTightness: 1.05,
      atmosphere: 0.55,
      backdropBlur: 60,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.7,
      bloomThreshold: 0.2,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 3 — Energy / drop looks
  // ----------------------------------------------------------------
  {
    name: "Core Mass",
    subtitle: "Dense particle core, big bass",
    accent: 195,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.2,
      cameraSpin: 0.28,
      starSize: 0.28,
      intensity: 1.4,
      opacity: 0.92,
      saturation: 0.85,
      bassGravity: 2.4,
      midCurl: 1.2,
      trebleShimmer: 0.8,
      cymaticStrength: 2.6,
      flowSpeed: 0.5,
      fieldTightness: 0.74,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
      showShockwave: false,
      showStarbursts: true,
    },
  },
  {
    name: "Shock Bloom",
    subtitle: "Beat-driven explosions",
    accent: 24,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "bloom",
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 1.25,
      cameraSpin: 0.4,
      starSize: 0.24,
      intensity: 1.4,
      opacity: 0.94,
      saturation: 0.85,
      bassGravity: 2.6,
      midCurl: 1.4,
      trebleShimmer: 1.0,
      cymaticStrength: 3.2,
      flowSpeed: 0.7,
      fieldTightness: 0.85,
      atmosphere: 0.75,
      bloomEnabled: true,
      bloomStrength: 1.5,
      bloomRadius: 0.6,
      bloomThreshold: 0.1,
      showShockwave: true,
      showStarbursts: true,
    },
  },
  {
    name: "Storm",
    subtitle: "Particles + strings, big amplitude",
    accent: 230,
    values: {
      // Trimmed: was lattice + aurora on top. Too many elements at once.
      layerParticles: true,
      layerStrings: true,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0.5,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 2.2,
      midCurl: 1.4,
      trebleShimmer: 1.2,
      cymaticStrength: 2.8,
      flowSpeed: 1.3,
      fieldTightness: 1.0,
      atmosphere: 0.75,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: true,
    },
  },

  // ----------------------------------------------------------------
  // ROW 4 — Warm / experimental
  // ----------------------------------------------------------------
  {
    name: "Lava Flow",
    subtitle: "Hot orange currents, no clutter",
    accent: 18,
    values: {
      // Trimmed: was hybrid + aurora. Too busy.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "lava",
      cameraMode: "orbit",
      cameraTilt: 0.25,
      cameraZoom: 1.0,
      cameraSpin: 0.2,
      paletteHue: 14,
      starSize: 0.2,
      intensity: 1.35,
      opacity: 0.92,
      saturation: 0.95,
      bassGravity: 1.8,
      midCurl: 1.6,
      trebleShimmer: 0.6,
      cymaticStrength: 1.6,
      flowSpeed: 0.6,
      fieldTightness: 0.9,
      atmosphere: 0.65,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.55,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 5 — v2 hero scenes (DNA / Vortex / Tide / Orbital)
  // ----------------------------------------------------------------
  {
    name: "DNA Helix",
    subtitle: "Spinning double helix, side view",
    accent: 290,
    values: {
      // DNA only. The helix is the entire visual.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: true,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      colorMode: 1,
      conceptMode: "none",
      // Orbit camera framed from the SIDE so the helix's twist is visible.
      // (Looking down the helix axis collapses the depth and you just see
      // a starburst of overlapping rungs.) Slight horizon tilt + slow spin
      // gives a cinematic "rotating molecule" feel.
      cameraMode: "orbit",
      cameraTilt: 0.32,   // slight horizon tilt — looking from the side
      cameraZoom: 0.8,    // pull back so the full helix fits in frame
      cameraSpin: 0.3,    // slow auto-spin for cinematic motion
      starSize: 0.22,
      intensity: 1.5,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 1.4,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.12,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Pulse Vortex",
    subtitle: "Inward spiral, kicks explode outward",
    accent: 320,
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: true,
      layerTide: false,
      layerOrbital: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.65, // looking down on the vortex from a slight angle
      cameraZoom: 0.85,
      cameraSpin: 0.15,
      starSize: 0.22,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.4,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 2.5,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Audio Tide",
    subtitle: "Live spectrum as terrain you fly over",
    accent: 200,
    controlProfile: "mesh",
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: true,
      layerOrbital: false,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.78, // high tilt — looking down at the terrain
      cameraZoom: 0.55, // pulled back so the whole spectrum is in view
      cameraSpin: 0.0,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.0,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 0.8,
      flowSpeed: 0.6,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Orbital Rings",
    subtitle: "Concentric bands, cinematic god view",
    accent: 50,
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: true,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 0.65,
      cameraSpin: 0.18,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.88,
      bassGravity: 1.6,
      midCurl: 1.2,
      trebleShimmer: 1.4,
      cymaticStrength: 1.2,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 6 — v3 cosmic / immersive scenes
  // ----------------------------------------------------------------
  {
    name: "Galaxy Flythrough",
    subtitle: "Spiral arms + warp stars",
    accent: 220,
    values: {
      // Galaxy disc + world-space warp streaks. Particle cloud OFF —
      // the disc IS the cloud, and the streaks supply foreground motion.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: true,
      layerAuroraForest: false,
      colorMode: 1,
      conceptMode: "none",
      // Slight tilt-up looking into one spiral arm, cinematic slow drift.
      cameraMode: "orbit",
      cameraTilt: 0.35,
      cameraZoom: 0.75,
      cameraSpin: 0.12,
      paletteHue: 200,
      starSize: 0.18,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 1.0,
      flowSpeed: 1.2,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Aurora Forest",
    subtitle: "Stand inside a forest of dancing ribbons",
    accent: 145,
    values: {
      // Vertical aurora ribbons standing in a circle around the camera.
      // Pure ribbons — no particle cloud, no other layers, so the user
      // experiences being IN the forest looking out at the curtains.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: true,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.42, // slight horizon view
      cameraZoom: 0.55, // pulled back so multiple ribbons are visible
      cameraSpin: 0.1,  // very slow auto-spin to invite exploration
      paletteHue: 145,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.95,
      bassGravity: 1.0,
      midCurl: 1.4,
      trebleShimmer: 1.6,
      cymaticStrength: 0.8,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 7 — v4 organic / immersive scenes
  // ----------------------------------------------------------------
  {
    name: "Boid Flock",
    subtitle: "Glowing swarm reacts to every kick",
    accent: 100,
    values: {
      // Boid swarm only. Self-organizes into a coherent flock; bass kicks
      // tighten it into a defensive ball, treble fans them apart.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: true,
      layerWind: false,
      layerMercury: false,
      colorMode: 2, // velocity — boids hue-shift by their own speed
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.7,
      cameraSpin: 0.18, // slow auto-orbit so the swarm reads from all sides
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Wind Tunnel",
    subtitle: "Organic flow, beats reverse direction",
    accent: 30,
    values: {
      // Camera-locked wind tunnel. Particles flow toward the lens; a
      // strong beat momentarily reverses the direction so the kick
      // visibly "blows the wind backward."
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: true,
      layerMercury: false,
      colorMode: 2,
      conceptMode: "none",
      // Static framing — the tunnel motion IS the visual; camera should
      // hold still so the flow direction reads clearly.
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      paletteHue: 28,
      starSize: 0.2,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.6,   // more curl = more streamline weave
      trebleShimmer: 1.3,
      cymaticStrength: 1.0,
      flowSpeed: 1.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Mercury Pool",
    subtitle: "Reflective liquid waves to the music",
    accent: 195,
    controlProfile: "mesh",
    values: {
      // Wide reflective surface in front of the camera. Treble drops
      // ripples into the pool; bass heaves the whole surface.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: true,
      colorMode: 0, // ice — cool blue/silver glacier tint suits liquid metal
      conceptMode: "none",
      // Glancing-angle camera so the surface reads as horizontal liquid.
      cameraMode: "orbit",
      cameraTilt: 0.18, // very low tilt — looking across the pool
      cameraZoom: 0.65,
      cameraSpin: 0.12, // gentle drift around the pool
      paletteHue: 200,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.95,
      saturation: 0.7,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.4,
      cymaticStrength: 1.2,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Mountain Range",
    subtitle: "Fly through endless terrain",
    accent: 35,
    controlProfile: "mesh",
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: true,
      colorMode: 1,
      conceptMode: "none",
      // Camera floats above the terrain at a gentle downward tilt;
      // flowSpeed in the params drives forward flight through the range.
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.55,
      cameraSpin: 0,
      paletteHue: 30,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 1.0,
      flowSpeed: 1.2,    // forward flight speed; 0 to freeze the range
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },

  // ----------------------------------------------------------------
  // ROW 8 — Emergence physics scenes (lava-lamp / blob morphing)
  // ----------------------------------------------------------------
  // These scenes use the new self-organizing physics: cohesion to a
  // target shape + noise + bass repulsion. Reads as a fluid blob
  // rather than orbital motion. All built on the particle layer with
  // particleMode set to "emergence".
  {
    name: "Fire Core",
    subtitle: "Volumetric flame inside a glassy sphere",
    accent: 18,
    controlProfile: "energyOrb",
    mood: "Cinematic",
    variants: [
      {
        name: "Solar",
        accent: 42,
        values: {
          energyOrbPrimaryColor: "#fff2a0",
          energyOrbSecondaryColor: "#ff5a00",
          energyOrbDensity: 1.55,
          energyOrbAtmosphereGlow: 0.18,
          bloomStrength: 1.2,
        },
      },
      {
        name: "Inferno",
        accent: 5,
        values: {
          energyOrbPrimaryColor: "#ffd36a",
          energyOrbSecondaryColor: "#ff1100",
          energyOrbDensity: 1.75,
          energyOrbFractalDecay: -13.4,
          energyOrbAtmosphereGlow: 0.24,
          fxRgbShift: 0.026,
        },
      },
    ],
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerEnergyOrb: true,
      layerWaveform: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.44,
      cameraZoom: 0.86,
      cameraSpin: 0.18,
      paletteHue: 18,
      starSize: 0.18,
      intensity: 1.35,
      opacity: 0.95,
      saturation: 0.95,
      bassGravity: 1.7,
      midCurl: 1.25,
      trebleShimmer: 1.2,
      cymaticStrength: 1.1,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.38,
      bloomEnabled: true,
      bloomStrength: 1.08,
      bloomRadius: 0.58,
      bloomThreshold: 0.16,
      postFxEnabled: true,
      fxVignette: 0.18,
      fxRgbShift: 0.014,
      fxFilmGrain: 0.02,
      showShockwave: false,
      showStarbursts: false,
      energyOrbSize: 0.3,
      energyOrbPrimaryColor: "#ffd36a",
      energyOrbSecondaryColor: "#ff2a00",
      energyOrbDensity: 1.55,
      energyOrbSpeed: 0.58,
      energyOrbRotation: 0.46,
      energyOrbFractalIters: 4,
      energyOrbFractalScale: 0.75,
      energyOrbFractalDecay: -14.0,
      energyOrbSmoothness: 0.012,
      energyOrbAsymmetry: 0.45,
      energyOrbAtmosphereGlow: 0.16,
      energyOrbAtmosphereLevel: 0.95,
      energyOrbAtmosphereScale: 1.04,
    },
  },
  {
    name: "Ion Bloom",
    subtitle: "Cyan-violet volumetric light bloom",
    accent: 188,
    controlProfile: "energyOrb",
    mood: "Cosmic",
    variants: [
      {
        name: "Cyan",
        accent: 182,
        values: {
          energyOrbPrimaryColor: "#00ffee",
          energyOrbSecondaryColor: "#31a6ff",
          energyOrbDensity: 0.9,
          energyOrbAtmosphereGlow: 0.14,
        },
      },
      {
        name: "Violet",
        accent: 280,
        values: {
          energyOrbPrimaryColor: "#b9f7ff",
          energyOrbSecondaryColor: "#8a2bff",
          energyOrbDensity: 1.0,
          energyOrbFractalDecay: -15.4,
          fxRgbShift: 0.026,
        },
      },
    ],
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerEnergyOrb: true,
      layerWaveform: false,
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.84,
      cameraSpin: 0.12,
      paletteHue: 190,
      starSize: 0.14,
      intensity: 1.2,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.35,
      trebleShimmer: 1.7,
      cymaticStrength: 1.1,
      flowSpeed: 0.65,
      fieldTightness: 1.0,
      atmosphere: 0.42,
      bloomEnabled: true,
      bloomStrength: 1.08,
      bloomRadius: 0.6,
      bloomThreshold: 0.16,
      postFxEnabled: true,
      fxVignette: 0.1,
      fxRgbShift: 0.014,
      fxFilmGrain: 0.012,
      showShockwave: false,
      showStarbursts: false,
      energyOrbSize: 0.3,
      energyOrbPrimaryColor: "#00ffee",
      energyOrbSecondaryColor: "#8a2bff",
      energyOrbDensity: 0.82,
      energyOrbSpeed: 0.42,
      energyOrbRotation: 0.28,
      energyOrbFractalIters: 3,
      energyOrbFractalScale: 0.75,
      energyOrbFractalDecay: -16.7,
      energyOrbSmoothness: 0.05,
      energyOrbAsymmetry: 0.45,
      energyOrbAtmosphereGlow: 0.12,
      energyOrbAtmosphereLevel: 0.88,
      energyOrbAtmosphereScale: 1.045,
    },
  },
  {
    name: "Lava Sphere",
    subtitle: "Breathing blob, kicks blow it open",
    accent: 25,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "sphere",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.2,
      paletteHue: 22,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.8,
      midCurl: 1.4,
      trebleShimmer: 1.2,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: true,
    },
  },
  {
    name: "Plasma Torus",
    subtitle: "Spinning donut deforms with bass",
    accent: 280,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "torus",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.7,
      cameraSpin: 0.18,
      paletteHue: 280,
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.5,
      midCurl: 1.6,
      trebleShimmer: 1.3,
      cymaticStrength: 1.4,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.14,
      showShockwave: false,
      showStarbursts: true,
    },
  },
  {
    name: "Drift Cube",
    subtitle: "Geometric mass with melting edges",
    accent: 175,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "cube",
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.42,
      cameraZoom: 0.6,
      cameraSpin: 0.22,
      paletteHue: 175,
      starSize: 0.18,
      intensity: 1.35,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 0.9,
      cymaticStrength: 1.2,
      flowSpeed: 0.7,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.55,
      bloomThreshold: 0.16,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Ribbon Drift",
    subtitle: "Long curving form, swept by mids",
    accent: 145,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "ribbon",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.65,
      cameraSpin: 0.16,
      paletteHue: 145,
      starSize: 0.2,
      intensity: 1.35,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.2,
      midCurl: 1.6,
      trebleShimmer: 1.3,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Lava Blob",
    subtitle: "Pure organic flow, no shape lock",
    accent: 320,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "blob",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.7,
      cameraSpin: 0.2,
      paletteHue: 320,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.2,
      midCurl: 1.4,
      trebleShimmer: 1.0,
      cymaticStrength: 1.8,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: true,
    },
  },

  // ----------------------------------------------------------------
  // ROW 9 — Hybrid scenes that showcase the waveform overlay
  // ----------------------------------------------------------------
  {
    name: "Waveform Glow",
    subtitle: "Particle field + dancing audio waveform",
    accent: 195,
    values: {
      // Liquid Mass-style cloud underneath, with the waveform overlay
      // on top to show the actual audio shape. Together they read as
      // "the music made visible" in the most literal sense.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerWaveform: true,
      waveformStyle: "line",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 1.0,
      cameraSpin: 0.4,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.6,
      trebleShimmer: 1.2,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.6,
      bloomThreshold: 0.16,
      showShockwave: false,
      showStarbursts: true,
    },
  },
  {
    name: "Pulse Halo",
    subtitle: "Vortex with circular waveform halo",
    accent: 320,
    values: {
      // Pulse Vortex with the radial waveform sitting like a halo on top.
      // The vortex's bass-driven breath + the waveform's circular pulse
      // sync into a single hypnotic rhythm.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: true,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerWaveform: true,
      waveformStyle: "radial",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.65,
      cameraZoom: 0.85,
      cameraSpin: 0.15,
      starSize: 0.22,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.4,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 2.5,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: false,
    },
  },
  {
    name: "Quantum Core",
    subtitle: "Volumetric core, cool reactor glow",
    accent: 222,
    controlProfile: "energyOrb",
    mood: "Cosmic",
    values: {
      layerEnergyOrb: true,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.58,
      cameraZoom: 0.82,
      cameraSpin: 0.18,
      starSize: 0.16,
      intensity: 1.42,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.2,
      midCurl: 1.4,
      trebleShimmer: 1.7,
      cymaticStrength: 1.4,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.25,
      bloomRadius: 0.62,
      bloomThreshold: 0.13,
      showShockwave: false,
      showStarbursts: false,
      energyOrbSize: 0.46,
      energyOrbPrimaryColor: "#74f7ff",
      energyOrbSecondaryColor: "#7846ff",
      energyOrbDensity: 1.55,
      energyOrbSpeed: 0.72,
      energyOrbRotation: 0.55,
      energyOrbFractalIters: 5,
      energyOrbFractalScale: 0.86,
      energyOrbFractalDecay: -13.8,
      energyOrbSmoothness: 0.015,
      energyOrbAsymmetry: 0.34,
      energyOrbAtmosphereGlow: 0.36,
      energyOrbAtmosphereLevel: 0.9,
      energyOrbAtmosphereScale: 1.05,
    },
  },
  {
    name: "Volumetric Sphere",
    subtitle: "Hue-shift audio sphere with folded surface light",
    accent: 24,
    sceneType: "texturedSphere",
    controlProfile: "texturedSphere",
    mood: "Trippy",
    variants: [
      {
        name: "Amber",
        accent: 24,
        values: {
          paletteHue: 24,
          sphereBaseColor: "#ff7c2c",
          sphereAccentColor: "#ffd36a",
          sphereNoiseSpeed: 1.1,
          sphereColorIntensity: 2.15,
        },
      },
      {
        name: "Ultraviolet",
        accent: 276,
        values: {
          paletteHue: 276,
          sphereBaseColor: "#8a2bff",
          sphereAccentColor: "#00ffee",
          sphereNoiseSpeed: 0.92,
          sphereColorIntensity: 2.35,
        },
      },
    ],
    values: {
      paletteHue: 24,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.52,
      cameraZoom: 0.86,
      cameraSpin: 0.3,
      starSize: 0.18,
      intensity: 1.28,
      opacity: 0.96,
      saturation: 0.96,
      bassGravity: 1.35,
      midCurl: 1.35,
      trebleShimmer: 1.65,
      cymaticStrength: 1.35,
      flowSpeed: 1.6,
      atmosphere: 0.62,
      bloomEnabled: true,
      bloomStrength: 1.22,
      bloomRadius: 0.64,
      bloomThreshold: 0.12,
      sphereDetail: 144,
      sphereSize: 1.9,
      sphereDisplacement: 0.62,
      sphereVertexAudioMix: 0.9,
      sphereNoiseScale: 2.8,
      sphereNoiseIntensity: 0.72,
      sphereNoiseSpeed: 1.05,
      sphereWaveFrequency: 1.05,
      sphereWaveSpeed: 1.35,
      sphereColorIntensity: 2.15,
      sphereMotionSmoothing: 0.68,
      sphereRotationSpeed: 0.32,
      sphereBaseColor: "#ff7c2c",
      sphereAccentColor: "#ffd36a",
      sphereSolidInterior: false,
    },
  },
  {
    name: "Colorful Sphere",
    subtitle: "Audio-reactive surface, rainbow tension",
    accent: 292,
    sceneType: "texturedSphere",
    controlProfile: "texturedSphere",
    mood: "Trippy",
    values: {
      paletteHue: 292,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.48,
      cameraZoom: 0.9,
      cameraSpin: 0.28,
      starSize: 0.18,
      intensity: 1.45,
      opacity: 0.96,
      saturation: 0.98,
      bassGravity: 1.2,
      midCurl: 1.45,
      trebleShimmer: 1.4,
      flowSpeed: 0.9,
      atmosphere: 0.52,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.58,
      bloomThreshold: 0.14,
      sphereDetail: 160,
      sphereSize: 2.05,
      sphereDisplacement: 0.48,
      sphereVertexAudioMix: 1.1,
      sphereNoiseScale: 3.2,
      sphereNoiseIntensity: 0.85,
      sphereNoiseSpeed: 0.85,
      sphereWaveFrequency: 1.2,
      sphereWaveSpeed: 1.05,
      sphereColorIntensity: 2.4,
      sphereMotionSmoothing: 0.72,
      sphereRotationSpeed: 0.5,
      sphereBaseColor: "",
      sphereAccentColor: "",
      sphereSolidInterior: true,
    },
  },
  {
    name: "Superform",
    subtitle: "Fibonacci sphere with Gielis superformula on azimuth",
    accent: 38,
    sceneType: "superform",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 35,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0.04,
      starSize: 0.2,
      intensity: 1.62,
      opacity: 0.78,
      saturation: 0.92,
      bassGravity: 1.05,
      midCurl: 1.1,
      trebleShimmer: 1.1,
      cymaticStrength: 1.6,
      flowSpeed: 1.5,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.74,
      bloomRadius: 0.54,
      bloomThreshold: 0.34,
      mathParticleCount: 38_000,
      mathPointSize: 0.7,
      mathAmplitude: 0.56,
      mathFrequency: 1,
      mathRotationSpeed: 0.34,
      mathDepthScale: 0.68,
      mathWarmth: 0.84,
    },
    variants: [
      { name: "Sphere", accent: 36, values: { mathAmplitude: 0.36, mathFrequency: 1, cameraSpin: 0.04 } },
      { name: "Petals", accent: 312, values: { mathFrequency: 3, mathAmplitude: 0.78 } },
      { name: "Dense", accent: 48, values: { mathParticleCount: 70_000, mathPointSize: 0.58 } },
    ],
  },
  {
    name: "Spiral Vortex",
    subtitle: "Nested torus flow with gold inner ring",
    accent: 196,
    sceneType: "spiralVortex",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 196,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.95,
      cameraSpin: 0.04,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.15,
      trebleShimmer: 1.05,
      cymaticStrength: 1.6,
      flowSpeed: 1.6,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.82,
      bloomRadius: 0.52,
      bloomThreshold: 0.38,
      mathParticleCount: 56_000,
      mathPointSize: 0.7,
      mathAmplitude: 0.62,
      mathFrequency: 1,
      mathRotationSpeed: 0.36,
      mathDepthScale: 0.72,
      mathWarmth: 0.42,
    },
    variants: [
      { name: "Drain", accent: 42, values: { mathFrequency: 2.4, mathAmplitude: 0.82, bassGravity: 1.3 } },
      { name: "Calm", accent: 190, values: { mathRotationSpeed: 0.16, flowSpeed: 0.9 } },
      { name: "Wide", accent: 32, values: { cameraZoom: 0.82, mathParticleCount: 76_000, mathPointSize: 0.52 } },
    ],
  },
  {
    name: "Hopf Fibration",
    subtitle: "S3 stereographic projection - linked Hopf fiber rings",
    accent: 38,
    sceneType: "hopfFibration",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 38,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0.04,
      starSize: 0.18,
      intensity: 1.7,
      opacity: 0.82,
      saturation: 0.84,
      bassGravity: 1.0,
      midCurl: 1.05,
      trebleShimmer: 1.05,
      cymaticStrength: 1.2,
      flowSpeed: 1.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 0.72,
      bloomRadius: 0.42,
      bloomThreshold: 0.36,
      mathParticleCount: 42_000,
      mathPointSize: 0.42,
      mathAmplitude: 0.42,
      mathFrequency: 1,
      mathRotationSpeed: 0.34,
      mathDepthScale: 0.74,
      mathWarmth: 0.78,
    },
    variants: [
      { name: "Cool", accent: 205, values: { mathWarmth: 0.22, paletteHue: 205 } },
      { name: "Topology", accent: 38, values: { mathParticleCount: 62_000, mathPointSize: 0.36, bloomStrength: 0.4 } },
      { name: "Halo", accent: 52, values: { mathAmplitude: 0.58, bloomStrength: 0.52, bloomThreshold: 0.52 } },
    ],
  },
  {
    name: "Hopf Fibration (Linked Strands)",
    subtitle: "Park-parameterized fibers, continuous SO(4) re-fibration",
    accent: 268,
    sceneType: "hopfTubes",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 268,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.52,
      cameraZoom: 0.82,
      cameraSpin: 0.22,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.55,
      trebleShimmer: 1.6,
      cymaticStrength: 1.85,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.64,
      bloomEnabled: true,
      bloomStrength: 1.15,
      bloomRadius: 0.66,
      bloomThreshold: 0.15,
      feedbackStrength: 0.03,
      feedbackDecay: 0.968,
    },
  },
  {
    name: "Fibonacci Sequence",
    subtitle: "Golden-angle phyllotaxis spiral with nautilus growth flow",
    accent: 45,
    sceneType: "fibonacciSequence",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 42,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.95,
      cameraSpin: 0.045,
      starSize: 0.18,
      intensity: 1.66,
      opacity: 0.82,
      saturation: 0.9,
      bassGravity: 1.05,
      midCurl: 1.05,
      trebleShimmer: 1.1,
      cymaticStrength: 1.2,
      flowSpeed: 1.55,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 0.72,
      bloomRadius: 0.46,
      bloomThreshold: 0.36,
      mathParticleCount: 40_000,
      mathPointSize: 0.44,
      mathAmplitude: 0.5,
      mathFrequency: 1,
      mathRotationSpeed: 0.32,
      mathDepthScale: 0.82,
      mathWarmth: 0.86,
    },
    variants: [
      { name: "Sunflower", accent: 48, values: { mathDepthScale: 0.38, mathAmplitude: 0.34, cameraZoom: 1.06 } },
      { name: "Nautilus", accent: 34, values: { mathDepthScale: 1, mathAmplitude: 0.62, flowSpeed: 1.3 } },
      { name: "Dense", accent: 52, values: { mathParticleCount: 68_000, mathPointSize: 0.36, bloomStrength: 0.42 } },
    ],
  },

  // ----------------------------------------------------------------
  // Math Based — remaining Immersiom formations (GPGPU engine)
  // Each routes to a SuperformGPUScene with a distinct formation ID.
  // ----------------------------------------------------------------
  {
    name: "Radial Pulse",
    subtitle: "Fibonacci sphere wineglass with concentric ring waves",
    accent: 42,
    sceneType: "radialPulse",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 35, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.04,
      starSize: 0.2, intensity: 1.68, opacity: 0.78, saturation: 0.92,
      bassGravity: 1.15, midCurl: 1.05, trebleShimmer: 1.0,
      cymaticStrength: 1.4, flowSpeed: 1.55, fieldTightness: 1.0,
      atmosphere: 0.6, bloomEnabled: true, bloomStrength: 0.74,
      bloomRadius: 0.54, bloomThreshold: 0.34,
      mathParticleCount: 38_000,
      mathPointSize: 0.72,
      mathAmplitude: 0.58,
      mathFrequency: 1,
      mathRotationSpeed: 0.32,
      mathDepthScale: 0.68,
      mathWarmth: 0.84,
    },
    variants: [
      { name: "Float", accent: 42, values: { cameraSpin: 0.03, mathRotationSpeed: 0.16, flowSpeed: 0.9 } },
      { name: "Cool", accent: 205, values: { mathWarmth: 0.28, paletteHue: 205 } },
      { name: "Rings", accent: 50, values: { mathFrequency: 2.4, mathAmplitude: 0.72, bloomStrength: 0.85 } },
    ],
  },
  {
    name: "Sine Surge",
    subtitle: "Layered sine waves rolling along the sphere",
    accent: 162,
    sceneType: "sineSurge",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 162, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.55, cameraZoom: 0.88, cameraSpin: 0.22,
      starSize: 0.15, intensity: 0.82, opacity: 0.64, saturation: 0.88,
      bassGravity: 1.2, midCurl: 1.7, trebleShimmer: 1.35,
      cymaticStrength: 2.0, flowSpeed: 1.05, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 0.45,
      bloomRadius: 0.42, bloomThreshold: 0.5,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Stellarator",
    subtitle: "Helical flux ribbons twist around a sphere",
    accent: 290,
    sceneType: "stellarator",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 290, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.6, cameraZoom: 0.82, cameraSpin: 0.28,
      starSize: 0.15, intensity: 0.78, opacity: 0.62, saturation: 0.9,
      bassGravity: 1.25, midCurl: 1.65, trebleShimmer: 1.45,
      cymaticStrength: 2.1, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.66, bloomEnabled: true, bloomStrength: 0.44,
      bloomRadius: 0.42, bloomThreshold: 0.52,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Plasma Sheet",
    subtitle: "Flat plasma sheet warped by audio currents",
    accent: 332,
    sceneType: "plasmaSheet",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 332, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.42, cameraZoom: 0.92, cameraSpin: 0.14,
      starSize: 0.14, intensity: 0.74, opacity: 0.6, saturation: 0.92,
      bassGravity: 1.4, midCurl: 1.5, trebleShimmer: 1.4,
      cymaticStrength: 2.2, flowSpeed: 1.1, fieldTightness: 0.95,
      atmosphere: 0.7, bloomEnabled: true, bloomStrength: 0.42,
      bloomRadius: 0.42, bloomThreshold: 0.55,
      feedbackStrength: 0.005, feedbackDecay: 0.984,
    },
  },
  {
    name: "Noise Bloom",
    subtitle: "Curl-noise blooms unfolding from the center",
    accent: 86,
    sceneType: "noiseBloom",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 86, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.88, cameraSpin: 0.2,
      starSize: 0.15, intensity: 0.8, opacity: 0.62, saturation: 0.86,
      bassGravity: 1.15, midCurl: 1.85, trebleShimmer: 1.6,
      cymaticStrength: 1.85, flowSpeed: 0.95, fieldTightness: 1.0,
      atmosphere: 0.6, bloomEnabled: true, bloomStrength: 0.46,
      bloomRadius: 0.42, bloomThreshold: 0.5,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Harmonic Shell",
    subtitle: "Integer azimuthal modes ripple across the sphere",
    accent: 220,
    sceneType: "harmonicShell",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 220, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.52, cameraZoom: 0.86, cameraSpin: 0.24,
      starSize: 0.15, intensity: 0.82, opacity: 0.64, saturation: 0.88,
      bassGravity: 1.1, midCurl: 1.55, trebleShimmer: 1.75,
      cymaticStrength: 2.0, flowSpeed: 0.92, fieldTightness: 1.0,
      atmosphere: 0.64, bloomEnabled: true, bloomStrength: 0.45,
      bloomRadius: 0.42, bloomThreshold: 0.5,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Strange Attractor",
    subtitle: "Chaotic attractor curve sampled by particles",
    accent: 308,
    sceneType: "strangeAttractor",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 308, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.62, cameraZoom: 0.78, cameraSpin: 0.3,
      starSize: 0.14, intensity: 0.86, opacity: 0.62, saturation: 0.87,
      bassGravity: 1.0, midCurl: 1.5, trebleShimmer: 1.65,
      cymaticStrength: 1.7, flowSpeed: 1.05, fieldTightness: 1.05,
      atmosphere: 0.58, bloomEnabled: true, bloomStrength: 0.44,
      bloomRadius: 0.42, bloomThreshold: 0.52,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Lorenz Drift",
    subtitle: "Lorenz lobes drifting through the origin",
    accent: 188,
    sceneType: "lorenzDrift",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 188, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.55, cameraZoom: 0.84, cameraSpin: 0.26,
      starSize: 0.14, intensity: 0.82, opacity: 0.62, saturation: 0.88,
      bassGravity: 1.2, midCurl: 1.45, trebleShimmer: 1.45,
      cymaticStrength: 1.75, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 0.44,
      bloomRadius: 0.42, bloomThreshold: 0.52,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Organic Breath",
    subtitle: "Slow, alive breathing of a soft sphere",
    accent: 132,
    sceneType: "organicBreath",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 132, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.92, cameraSpin: 0.12,
      starSize: 0.15, intensity: 0.78, opacity: 0.66, saturation: 0.85,
      bassGravity: 1.35, midCurl: 1.3, trebleShimmer: 1.2,
      cymaticStrength: 1.6, flowSpeed: 0.82, fieldTightness: 0.95,
      atmosphere: 0.68, bloomEnabled: true, bloomStrength: 0.4,
      bloomRadius: 0.42, bloomThreshold: 0.55,
      feedbackStrength: 0.004, feedbackDecay: 0.986,
    },
  },
  {
    name: "Helical Twist",
    subtitle: "Double-helix twist with radial pulse on the sphere",
    accent: 248,
    sceneType: "helicalTwist",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 248, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.58, cameraZoom: 0.82, cameraSpin: 0.28,
      starSize: 0.15, intensity: 0.78, opacity: 0.62, saturation: 0.9,
      bassGravity: 1.3, midCurl: 1.6, trebleShimmer: 1.5,
      cymaticStrength: 2.0, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.64, bloomEnabled: true, bloomStrength: 0.44,
      bloomRadius: 0.42, bloomThreshold: 0.52,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },

  // ----------------------------------------------------------------
  // Math Based — GPGPU 250k-particle variants of the four flagship math
  // pads above. These route to SuperformGPUScene (engine-webgl) for a
  // denser nebula-like alternative aesthetic.
  // ----------------------------------------------------------------
  {
    name: "Superform (GPU)",
    subtitle: "GPGPU 250k-particle Gielis-morph nebula",
    accent: 200,
    sceneType: "superformGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 196, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.52, cameraZoom: 0.88, cameraSpin: 0.32,
      starSize: 0.15, intensity: 0.78, opacity: 0.62, saturation: 0.9,
      bassGravity: 1.25, midCurl: 1.8, trebleShimmer: 1.4,
      cymaticStrength: 2.1, flowSpeed: 0.95, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 0.42,
      bloomRadius: 0.42, bloomThreshold: 0.54,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },
  {
    name: "Hopf Fibration (GPU)",
    subtitle: "GPGPU 250k-particle Hopf bundle",
    accent: 258,
    sceneType: "hopfFibrationGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 258, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.78, cameraSpin: 0.26,
      starSize: 0.14, intensity: 0.75, opacity: 0.6, saturation: 0.88,
      bassGravity: 1.15, midCurl: 1.5, trebleShimmer: 1.7,
      cymaticStrength: 1.9, flowSpeed: 0.82, fieldTightness: 1.0,
      atmosphere: 0.66, bloomEnabled: true, bloomStrength: 0.42,
      bloomRadius: 0.42, bloomThreshold: 0.55,
      feedbackStrength: 0.005, feedbackDecay: 0.984,
    },
  },
  {
    name: "Fibonacci Sequence (GPU)",
    subtitle: "GPGPU 250k-particle accretion disc / phyllotaxis",
    accent: 42,
    sceneType: "fibonacciSequenceGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 42, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.58, cameraZoom: 0.9, cameraSpin: 0.18,
      starSize: 0.14, intensity: 0.82, opacity: 0.62, saturation: 0.86,
      bassGravity: 1.05, midCurl: 1.25, trebleShimmer: 1.55,
      cymaticStrength: 1.55, flowSpeed: 0.88, fieldTightness: 1.0,
      atmosphere: 0.58, bloomEnabled: true, bloomStrength: 0.4,
      bloomRadius: 0.42, bloomThreshold: 0.55,
      feedbackStrength: 0.005, feedbackDecay: 0.984,
    },
  },
  {
    name: "Radial Pulse Field (GPU)",
    subtitle: "GPGPU 250k-particle concentric radial shells",
    accent: 14,
    sceneType: "radialPulseGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 14, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.85, cameraSpin: 0.18,
      starSize: 0.15, intensity: 0.78, opacity: 0.62, saturation: 0.9,
      bassGravity: 1.7, midCurl: 1.2, trebleShimmer: 1.1,
      cymaticStrength: 1.9, flowSpeed: 1.0, fieldTightness: 1.05,
      atmosphere: 0.6, bloomEnabled: true, bloomStrength: 0.42,
      bloomRadius: 0.42, bloomThreshold: 0.54,
      feedbackStrength: 0.006, feedbackDecay: 0.982,
    },
  },

  // ----------------------------------------------------------------
  // Alt-engine curated scenes — one per orphan AltScene engine so the
  // engine is reachable from the Curated gallery. Each routes via
  // `sceneType` and starts on a flattering preset for that renderer.
  // ----------------------------------------------------------------
  {
    name: "Wireframe Terrain",
    subtitle: "Mesh Grid engine — perlin landscape with audio displacement",
    accent: 168,
    sceneType: "meshGrid",
    controlProfile: "mesh",
    mood: "Tech",
    values: {
      paletteHue: 168,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.62,
      cameraZoom: 0.78,
      cameraSpin: 0.14,
      starSize: 0.18,
      intensity: 1.35,
      opacity: 0.94,
      saturation: 0.85,
      bassGravity: 1.5,
      midCurl: 1.2,
      trebleShimmer: 1.0,
      cymaticStrength: 1.4,
      flowSpeed: 0.85,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.05,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },
  {
    name: "Wavy Form",
    subtitle: "Stacked rotating waveform polylines",
    accent: 290,
    sceneType: "wavyForm",
    controlProfile: "mathParticles",
    mood: "Dreamy",
    values: {
      paletteHue: 290,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.22,
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.92,
      bassGravity: 1.2,
      midCurl: 1.6,
      trebleShimmer: 1.5,
      cymaticStrength: 1.7,
      flowSpeed: 0.95,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.14,
    },
  },
  {
    name: "Dot Wall",
    subtitle: "Instanced dot wall, audio-displaced",
    accent: 320,
    sceneType: "dotGrid",
    controlProfile: "mathParticles",
    mood: "Tech",
    values: {
      paletteHue: 320,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.9,
      cameraSpin: 0.12,
      starSize: 0.22,
      intensity: 1.5,
      opacity: 0.96,
      saturation: 0.95,
      bassGravity: 1.45,
      midCurl: 1.3,
      trebleShimmer: 1.4,
      cymaticStrength: 1.6,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.15,
      bloomRadius: 0.55,
      bloomThreshold: 0.16,
    },
  },
  {
    name: "Frequency Hallway",
    subtitle: "Mirrored stacked spectrum bars",
    accent: 12,
    sceneType: "frequencyBars",
    controlProfile: "mathParticles",
    mood: "Hype",
    values: {
      paletteHue: 12,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 0.8,
      cameraSpin: 0.1,
      starSize: 0.22,
      intensity: 1.55,
      opacity: 0.96,
      saturation: 0.95,
      bassGravity: 1.7,
      midCurl: 1.4,
      trebleShimmer: 1.3,
      cymaticStrength: 1.8,
      flowSpeed: 0.95,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.25,
      bloomRadius: 0.6,
      bloomThreshold: 0.14,
    },
  },
  {
    name: "Spiral Galaxy",
    subtitle: "Galaxy engine — spiral arms, audio-pulsing core",
    accent: 230,
    sceneType: "galaxy",
    controlProfile: "mathParticles",
    mood: "Cosmic",
    values: {
      paletteHue: 230,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.75,
      cameraSpin: 0.18,
      starSize: 0.24,
      intensity: 1.55,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.3,
      trebleShimmer: 1.6,
      cymaticStrength: 1.9,
      flowSpeed: 0.92,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
      feedbackStrength: 0.035,
      feedbackDecay: 0.965,
    },
  },
];

// ---------------------------------------------------------------------------
// Mood + variant overlays
// ---------------------------------------------------------------------------
//
// Tagging existing scenes with a mood + a couple of variants is done as a
// post-pass keyed by name so we don't have to rewrite the 1200-line literal
// above. New scenes added in the future can declare `mood`/`variants`
// inline and skip this table entirely.
const SCENE_OVERLAYS: Record<
  string,
  { mood?: SceneMood; variants?: SceneVariant[] }
> = {
  // ------------- Universal-appeal defaults -------------
  "Liquid Mass": {
    mood: "Chill",
    variants: [
      { name: "Warm", accent: 25, values: { colorMode: 2, saturation: 0.95, atmosphere: 0.7 } },
      { name: "Hot",  accent: 0,  values: { colorMode: 2, intensity: 1.7, bassGravity: 1.8, bloomStrength: 1.2 } },
      { name: "Mono", accent: 210, values: { colorMode: 0, saturation: 0.55, atmosphere: 0.5 } },
    ],
  },
  Aurora: {
    mood: "Dreamy",
    variants: [
      { name: "Polar",  accent: 195, values: { colorMode: 3, trebleShimmer: 1.9, atmosphere: 0.8 } },
      { name: "Sunset", accent: 25,  values: { colorMode: 2, saturation: 1.0, intensity: 1.3 } },
      { name: "Violet", accent: 285, values: { colorMode: 1, saturation: 0.95 } },
    ],
  },
  "Warp Drive": {
    mood: "Hype",
    variants: [
      { name: "Chrome", accent: 200, values: { colorMode: 0, saturation: 0.5 } },
      { name: "Neon",  accent: 320, values: { colorMode: 1, intensity: 1.8, bloomStrength: 1.7 } },
      { name: "Pulse", accent: 215, values: { beatDolly: 1.4, flowSpeed: 3.2 } },
    ],
  },
  Cathedral: {
    mood: "Cinematic",
    variants: [
      { name: "Stone",  accent: 45,  values: { colorMode: 2, saturation: 0.6, atmosphere: 0.5 } },
      { name: "Aqua",   accent: 175, values: { colorMode: 3, saturation: 0.9 } },
      { name: "Crimson", accent: 355, values: { colorMode: 2, saturation: 0.95, intensity: 1.45 } },
    ],
  },

  // ------------- Mood / quiet -------------
  "Ghost Cloud": {
    mood: "Dreamy",
    variants: [
      { name: "Mist", accent: 215, values: { atmosphere: 0.55, opacity: 0.65 } },
      { name: "Rose", accent: 340, values: { colorMode: 2, saturation: 0.85 } },
    ],
  },
  Nebula: {
    mood: "Cosmic",
    variants: [
      { name: "Deep",   accent: 245, values: { saturation: 0.6, atmosphere: 0.55 } },
      { name: "Plasma", accent: 305, values: { colorMode: 1, intensity: 1.4, bloomStrength: 1.4 } },
    ],
  },
  "Core Mass":      { mood: "Chill" },
  "Shock Bloom":    {
    mood: "Hype",
    variants: [
      { name: "Reactive", values: { showShockwave: true, bloomStrength: 1.5, bassGravity: 1.8 } },
      { name: "Chill",    values: { showShockwave: false, bloomStrength: 0.9 } },
    ],
  },
  Storm: {
    mood: "Hype",
    variants: [
      { name: "Wild", values: { intensity: 1.8, midCurl: 2.0 } },
      { name: "Calm", values: { intensity: 1.0, midCurl: 0.9 } },
    ],
  },
  "Lava Flow": {
    mood: "Hype",
    variants: [
      { name: "Glowing", values: { bloomStrength: 1.6, intensity: 1.6 } },
      { name: "Smoulder", values: { saturation: 0.7, intensity: 1.0 } },
    ],
  },

  // ------------- Curated additions -------------
  "DNA Helix":          { mood: "Tech" },
  "Pulse Vortex":       { mood: "Hype" },
  "Audio Tide":         { mood: "Chill" },
  "Orbital Rings":      { mood: "Cosmic" },
  "Galaxy Flythrough":  { mood: "Cosmic" },
  "Aurora Forest":      { mood: "Dreamy" },
  "Boid Flock":         { mood: "Chill" },
  "Wind Tunnel":        { mood: "Hype" },
  "Mercury Pool":       { mood: "Trippy" },
  "Mountain Range":     { mood: "Cinematic" },
  "Lava Sphere":        { mood: "Hype" },
  "Plasma Torus":       { mood: "Trippy" },
  "Drift Cube":         { mood: "Tech" },
  "Ribbon Drift":       { mood: "Dreamy" },
  "Lava Blob":          { mood: "Trippy" },
  "Waveform Glow":      { mood: "Tech" },
  "Pulse Halo":         { mood: "Hype" },
};

// Apply overlays in place — runs once at module load.
for (const scene of SCENES) {
  const overlay = SCENE_OVERLAYS[scene.name];
  if (!overlay) continue;
  if (overlay.mood && !scene.mood) scene.mood = overlay.mood;
  if (overlay.variants && !scene.variants) scene.variants = overlay.variants;
}

const PARTICLE_BRIGHTNESS_LIFT_MOODS = new Set<SceneMood>([
  "Hype",
  "Chill",
  "Dreamy",
  "Trippy",
]);
const PARTICLE_BRIGHTNESS_LIFT_EXCLUDED_SCENES = new Set([
  "Wavy Form",
  "Mercury Pool",
  "Volumetric Sphere",
  "Colorful Sphere",
]);
const PARTICLE_BRIGHTNESS_LIFT_EXTRA_SCENES = new Set([
  "Drift Cube",
  "Waveform Glow",
]);

function liftBrightnessValue(value: number | undefined, fallback: number, scale: number, add: number, max: number): number {
  const base = typeof value === "number" ? value : fallback;
  return Math.min(max, base * scale + add);
}

function liftParticleBrightness(values: Partial<BioclastParams>, includeDefaults: boolean): void {
  if (includeDefaults || typeof values.intensity === "number") {
    values.intensity = liftBrightnessValue(values.intensity, 1.3, 1.28, 0.14, 2.35);
  }
  if (includeDefaults || typeof values.opacity === "number") {
    const opacity = liftBrightnessValue(values.opacity, 0.9, 1.08, 0.06, 1);
    values.opacity = Math.max(opacity, includeDefaults ? 0.92 : opacity);
  }
  if (includeDefaults || typeof values.bloomStrength === "number") {
    values.bloomEnabled = true;
    values.bloomStrength = Math.max(
      includeDefaults ? 1.22 : 0,
      liftBrightnessValue(values.bloomStrength, 1.0, 1.26, 0.16, 2.1),
    );
  }
  if (includeDefaults || typeof values.bloomThreshold === "number") {
    const baseThreshold = typeof values.bloomThreshold === "number" ? values.bloomThreshold : 0.18;
    values.bloomThreshold = Math.max(0.08, Math.min(0.14, baseThreshold * 0.68));
  }
}

for (const scene of SCENES) {
  const liftByMood = scene.mood ? PARTICLE_BRIGHTNESS_LIFT_MOODS.has(scene.mood) : false;
  const shouldLift =
    (liftByMood || PARTICLE_BRIGHTNESS_LIFT_EXTRA_SCENES.has(scene.name)) &&
    !PARTICLE_BRIGHTNESS_LIFT_EXCLUDED_SCENES.has(scene.name);
  if (!shouldLift) continue;
  liftParticleBrightness(scene.values, true);
  for (const variant of scene.variants ?? []) {
    liftParticleBrightness(variant.values, false);
  }
}

/**
 * Mood display order. Scenes without a mood fall under "All" at the end.
 */
export const SCENE_MOOD_ORDER: SceneMood[] = [
  "Math Based",
  "Hype",
  "Chill",
  "Dreamy",
  "Trippy",
  "Cinematic",
  "Cosmic",
  "Tech",
];

/**
 * Group scenes by mood for grid rendering. The returned order matches
 * `SCENE_MOOD_ORDER`; an "All" bucket gathers any scene without a mood
 * tag so nothing silently disappears from the UI.
 */
export function scenesByMood(): Array<{ mood: SceneMood | "All"; scenes: ScenePreset[] }> {
  const buckets = new Map<SceneMood | "All", ScenePreset[]>();
  for (const m of SCENE_MOOD_ORDER) buckets.set(m, []);
  buckets.set("All", []);
  for (const s of SCENES) {
    const k: SceneMood | "All" = s.mood ?? "All";
    buckets.get(k)!.push(s);
  }
  const out: Array<{ mood: SceneMood | "All"; scenes: ScenePreset[] }> = [];
  for (const m of SCENE_MOOD_ORDER) {
    const arr = buckets.get(m)!;
    if (arr.length) out.push({ mood: m, scenes: arr });
  }
  const all = buckets.get("All")!;
  if (all.length) out.push({ mood: "All", scenes: all });
  return out;
}

const SCENE_LAYER_DEFAULTS: Partial<BioclastParams> = {
  layerParticles: false,
  layerStrings: false,
  layerLattice: false,
  layerAurora: false,
  layerWarp: false,
  layerCathedral: false,
  layerDnaHelix: false,
  layerVortex: false,
  layerTide: false,
  layerOrbital: false,
  layerGalaxy: false,
  layerAuroraForest: false,
  layerBoids: false,
  layerWind: false,
  layerMercury: false,
  layerMountains: false,
  layerEnergyOrb: false,
  layerWaveform: false,
};

/**
 * Merge layer reset defaults, `scene.values`, then `variant.values` into a
 * single param object suitable for `Object.assign(renderer.params, …)`. Pass
 * `null` for `variant` to apply just the base scene.
 */
export function resolveSceneValues(
  scene: ScenePreset,
  variant: SceneVariant | null,
): Partial<BioclastParams> {
  if (!variant) return { ...SCENE_LAYER_DEFAULTS, ...scene.values };
  return { ...SCENE_LAYER_DEFAULTS, ...scene.values, ...variant.values };
}
